import * as React from "react"
import * as Yup from "yup"
import Form, { FormElement } from "@modules/form/components/Form"
import { MdArrowForward } from "react-icons/md"
import { CdnVendor } from "@modules/graphql/types"
import {
  Button,
  Notification,
  Text,
  StyledModalActions,
} from "gatsby-interface"
import {
  vercelIntegrationConfig as text,
  ui as uiText,
} from "@modules/locales/default.js"
import { useCreateIntegration } from "../../shared/hooks/useCreateIntegration"
import { FormattedMessage } from "@modules/locales"
import { VercelTeamField } from "../components/VercelTeamField"
import { VercelProjectField } from "../components/VercelProjectField"
import {
  normalizeVercelTeamId,
  parseVercelTeamId,
  getInitialVercelTeam,
} from "../utils"
import { useVercelTeamsForSiteQuery } from "../queries.generated"

const validationSchema = Yup.object().shape({
  vercelTeam: Yup.string(),
  vercelProject: Yup.string().required(uiText.messages.validationIsRequired),
})

export function ExistingIntegration({
  onClose,
  onValidate,
  siteId,
  initialVercelProject,
  initialVercelTeam,
  onSwitchToNew,
}) {
  const [createIntegration, { loading }] = useCreateIntegration(CdnVendor.Now)

  const { data, loading: teamLoading } = useVercelTeamsForSiteQuery({
    variables: {
      siteId,
    },
    fetchPolicy: `cache-and-network`,
  })

  const teams = data?.nowTeamsForGatsbySiteId ?? []

  const initialTeam = initialVercelTeam || getInitialVercelTeam(teams)

  const handleSubmit = values => {
    createIntegration(
      siteId,
      values.vercelProject,
      values.vercelProject,
      normalizeVercelTeamId(values.vercelTeam)
    ).then(({ data }) =>
      onValidate({
        success: data.setCDNConfig.success,
        errorType: data.setCDNConfig.errorType,
      })
    )
  }

  return (
    <Form
      initialValues={{
        vercelTeam: parseVercelTeamId(initialTeam),
        vercelProject: initialVercelProject,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setValues }) => {
        return (
          <FormElement applySpacing noValidate>
            <Text css={{ margin: 0 }}>{text.headers.connectToExisting}</Text>

            <Notification
              variant="SECONDARY"
              tone="BRAND"
              content={
                <div>
                  <FormattedMessage
                    message={text.messages.connectToNewSiteCallout}
                    tags={{
                      button: function(content) {
                        return (
                          <Button
                            onClick={onSwitchToNew}
                            variant="GHOST"
                            size="M"
                            css={{ padding: 0, fontSize: `inherit` }}
                            aria-label={text.actions.switchToNewSite}
                          >
                            {content}
                          </Button>
                        )
                      },
                    }}
                  />
                </div>
              }
            />

            <VercelTeamField
              siteId={siteId}
              name="vercelTeam"
              label={text.labels.vercelProjectTeam}
              required
              teams={teams}
              loading={teamLoading}
              onChange={e => {
                setValues({
                  vercelTeam: e.target.value,
                  vercelProject: ``,
                })
              }}
            />

            <VercelProjectField
              siteId={siteId}
              teamId={values.vercelTeam}
              name="vercelProject"
              label={text.labels.vercelProjectId}
              required
            />

            <StyledModalActions>
              <Button variant="SECONDARY" type={`reset`} onClick={onClose}>
                {uiText.actions.cancel}
              </Button>

              <Button
                type="submit"
                loading={loading}
                rightIcon={<MdArrowForward />}
              >
                {uiText.actions.save}
              </Button>
            </StyledModalActions>
          </FormElement>
        )
      }}
    </Form>
  )
}
